import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { ConnectedPosition } from '@angular/cdk/overlay';
import { catchError, debounceTime, of, Subject, switchMap, tap } from 'rxjs';
import { BoardContactService } from '../../../../services/board-contact.service';
import { BoardContact, NewCardContact, UpdateContactPayload } from '../../board-v3.types';
import {
  customFieldEmailValidator,
  customFieldPhoneValidator,
  normalizePhonePayloadValue,
} from '../../custom-field.validators';
import { formatContactPhone, getContactEmails, getContactName, getContactPhones } from '../contact.utils';
import { AddressInput } from '../../../../interfaces/card';
import { toAddressInput } from '../../address.utils';

export type ContactDraftForm = FormGroup<{
  first_name: FormControl<string>;
  last_name: FormControl<string>;
  middle_name: FormControl<string>;
  phones: FormArray<FormControl<string>>;
  emails: FormArray<FormControl<string>>;
  addresses: FormControl<AddressInput[]>;
}>;

/**
 * One contact of a card: nothing picked, an existing contact, a new one (`draft` only)
 * or an existing one being edited (`existing` + `draft`)
 */
export interface ContactPickerSlot {
  existing: BoardContact | null;
  draft: ContactDraftForm | null;
}

export function createEmptyContactSlot(): ContactPickerSlot {
  return { existing: null, draft: null };
}

export function createContactDraftForm(value: Partial<NewCardContact> = {}): ContactDraftForm {
  return new FormGroup({
    first_name: new FormControl(value.first_name ?? '', {
      nonNullable: true,
      validators: [Validators.required, Validators.maxLength(255)],
    }),
    last_name: new FormControl(value.last_name ?? '', { nonNullable: true, validators: [Validators.maxLength(255)] }),
    middle_name: new FormControl(value.middle_name ?? '', {
      nonNullable: true,
      validators: [Validators.maxLength(255)],
    }),
    phones: new FormArray((value.phones?.length ? value.phones : ['']).map(createContactPhoneControl)),
    emails: new FormArray((value.emails?.length ? value.emails : ['']).map(createContactEmailControl)),
    addresses: new FormControl(value.addresses ?? [], { nonNullable: true }),
  });
}

export function createContactPhoneControl(value = ''): FormControl<string> {
  return new FormControl(value, { nonNullable: true, validators: [customFieldPhoneValidator] });
}

export function createContactEmailControl(value = ''): FormControl<string> {
  return new FormControl(value, { nonNullable: true, validators: [customFieldEmailValidator] });
}

/** Draft prefilled with an existing contact, to edit it */
export function createContactEditForm(contact: BoardContact): ContactDraftForm {
  return createContactDraftForm({
    first_name: contact.first_name,
    last_name: contact.last_name,
    middle_name: contact.middle_name,
    phones: getContactPhones(contact),
    emails: getContactEmails(contact),
    addresses: (contact.addresses ?? []).map(toAddressInput),
  });
}

export function getContactDraftPayload(form: ContactDraftForm): NewCardContact {
  const raw = form.getRawValue();

  return {
    first_name: raw.first_name.trim(),
    last_name: raw.last_name.trim() || undefined,
    middle_name: raw.middle_name.trim() || undefined,
    phones: raw.phones.map((phone) => normalizePhonePayloadValue(phone)).filter(Boolean),
    emails: raw.emails.map((email) => email.trim().toLowerCase()).filter(Boolean),
    addresses: raw.addresses,
  };
}

/** Like the create payload, but cleared names are sent as `null` so they are removed */
export function getContactUpdatePayload(form: ContactDraftForm): UpdateContactPayload {
  const payload = getContactDraftPayload(form);
  return { ...payload, last_name: payload.last_name ?? null, middle_name: payload.middle_name ?? null };
}

/** Puts the search text into the field it looks like: phone, email or name */
function draftFromQuery(query: string): Partial<NewCardContact> {
  const value = query.trim();
  if (!value) return {};
  if (value.includes('@')) return { emails: [value] };
  if (/^[\d+\s()-]+$/.test(value) && /\d{3,}/.test(value.replace(/\D/g, ''))) return { phones: [value] };

  return { first_name: value };
}

@Component({
  selector: 'app-board-v3-contact-picker',
  templateUrl: './contact-picker.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class BoardV3ContactPickerComponent implements OnInit {
  @Input({ required: true }) slot!: ContactPickerSlot;
  /** Contacts already picked in other slots */
  @Input() excludeIds: string[] = [];
  @Input() removable = false;
  @Output() slotChange = new EventEmitter<ContactPickerSlot>();
  @Output() remove = new EventEmitter<void>();
  @ViewChild('origin') private origin?: ElementRef<HTMLElement>;
  @ViewChild('searchInput') private searchInput?: ElementRef<HTMLInputElement>;

  private readonly contactService = inject(BoardContactService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly search$ = new Subject<string>();

  protected readonly positions: ConnectedPosition[] = [
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 4 },
    { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -4 },
  ];
  protected query = '';
  protected results: BoardContact[] = [];
  protected searching = false;
  protected open = false;
  protected overlayWidth = 0;

  protected readonly getContactName = getContactName;
  protected readonly formatContactPhone = formatContactPhone;
  protected readonly getContactPhones = getContactPhones;
  protected readonly getContactEmails = getContactEmails;

  get draft(): ContactDraftForm | null {
    return this.slot.draft;
  }

  /** An existing contact whose data is being edited */
  get isEditing(): boolean {
    return !!this.slot.existing && !!this.slot.draft;
  }

  ngOnInit(): void {
    this.search$
      .pipe(
        debounceTime(300),
        tap(() => (this.searching = true)),
        switchMap((search) =>
          this.contactService.getContacts({ search, take: 10 }).pipe(catchError(() => of({ items: [] }))),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((response) => {
        this.results = response.items;
        this.searching = false;
      });
  }

  protected isExcluded(contact: BoardContact): boolean {
    return this.excludeIds.includes(contact.id);
  }

  protected getContactSummary(contact: BoardContact): string {
    return [...getContactPhones(contact).map(formatContactPhone), ...getContactEmails(contact)].join(', ');
  }

  protected onQueryChange(value: string): void {
    this.query = value;
    if (!value.trim()) {
      this.open = false;
      this.results = [];
      return;
    }

    this.openOverlay();
    this.searching = true;
    this.search$.next(value.trim());
  }

  protected onFocus(): void {
    if (this.query.trim()) this.openOverlay();
  }

  protected onOutsideClick(event: MouseEvent): void {
    const target = event.target as Node | null;
    if (target && this.origin?.nativeElement.contains(target)) return;

    this.open = false;
  }

  protected select(contact: BoardContact): void {
    if (this.isExcluded(contact)) return;

    this.reset();
    this.slotChange.emit({ existing: contact, draft: null });
  }

  protected createNew(): void {
    const draft = createContactDraftForm(draftFromQuery(this.query));
    this.reset();
    this.slotChange.emit({ existing: null, draft });
  }

  protected edit(): void {
    if (!this.slot.existing) return;

    this.slotChange.emit({ existing: this.slot.existing, draft: createContactEditForm(this.slot.existing) });
  }

  protected cancelEdit(): void {
    this.slotChange.emit({ existing: this.slot.existing, draft: null });
  }

  protected addPhone(): void {
    this.draft?.controls.phones.push(createContactPhoneControl());
  }

  protected addEmail(): void {
    this.draft?.controls.emails.push(createContactEmailControl());
  }

  protected removeRow(list: FormArray<FormControl<string>>, index: number): void {
    if (list.length > 1) list.removeAt(index);
    else list.at(0).setValue('');
    list.markAsDirty();
  }

  /** Back to the search to pick another contact */
  protected change(): void {
    this.slotChange.emit(createEmptyContactSlot());
    setTimeout(() => this.searchInput?.nativeElement.focus());
  }

  private openOverlay(): void {
    this.overlayWidth = this.origin?.nativeElement.getBoundingClientRect().width ?? 0;
    this.open = true;
  }

  private reset(): void {
    this.query = '';
    this.results = [];
    this.open = false;
  }
}
